import { Suspense, type ReactNode } from "react";

import { PageTitle } from "@/components/seo/PageTitle";
import { AppLoadingScreen } from "@/shared/layout/AppLoadingScreen";

/**
 * Sets the browser-tab title for routes that render bespoke chrome (no `<SeoHead>`
 * or `AdminPage` scaffold). Marketing, legal, sign-in, and admin routes set their own.
 *
 * The inner Suspense is load-bearing. Every route component is a lazy import, and
 * App.tsx wraps all of AppRoutes in a single Suspense — so with `node` and
 * `PageTitle` as bare siblings, navigating to a route whose chunk is still loading
 * unmounted the outgoing title and never committed the incoming one, leaving the
 * tab on the PREVIOUS route's title (or Helmet's generic marketing `defaultTitle`).
 * Boundary inside, title outside: `PageTitle` commits immediately and stays mounted
 * while the chunk loads.
 *
 * Lives in its own file rather than beside `AppRoutes` so the route module keeps
 * exporting only components (react-refresh/only-export-components).
 */
export const titled = (title: string, node: ReactNode): ReactNode => (
  <>
    <PageTitle title={title} />
    <Suspense fallback={<AppLoadingScreen />}>{node}</Suspense>
  </>
);
